import {InvokeCallback, InvokeCreator, Sender} from 'xstate';
import {ApiErrorKind} from '@practicegenius/http-clients-js/src/types';
import {isTeen} from '@practicegenius/domain/src/data/users/birthdate';
import {IMT} from '@practicegenius/pg-util/src/StringUtils';
import {objectKeysToCamelCase} from '@practicegenius/pg-util/src/ObjectUtils';
import {outgoingAccount, transformRegistration} from '../data/transformers';
import {Context, Event, EventSchema} from './machineTypes';
import lang from '../lang';
import {routes} from '../routes';
import {SocialOauthError} from '../data/types';

type Services = {
  updateRegistration: InvokeCreator<
    {
      token: Context['token'];
      apiClient: Context['apiClient'];
      account: Context['account'];
      navigator: Context['navigator'];
    },
    EventSchema
  >;
};

type RegistrationError = {
  errors: Record<string, string[]>;
  response: {
    data: SocialOauthError;
  };
};

export const handleRegistrationValidationError = (
  error: RegistrationError,
  context: Context,
  send: Sender<EventSchema>
) => {
  const {response} = error;

  switch (response.data.code) {
    // triggered by rewards numbers, codes, and emails that belong to already registered members
    case 'account_registered': {
      // for teenagers entering email - don't redirect to already registered screen and instead display error message.
      if (isTeen(context.account.birthdate as Date) && context.account.email) {
        return send({
          type: Event.HAS_VALIDATION_ERRORS,
          errors: {
            account: {email: lang.general.registrationError.emailAlreadyInUse},
          },
        });
      }
      return send({
        type: Event.SKIP_TO_ALREADY_REGISTERED,
        message: response.data.message,
        hint: response.data.hint,
      });
    }
    case 'social_registered':
      return send({
        type: Event.SKIP_TO_SOCIAL_ALREADY_REGISTERED,
        message: response.data.message,
        hint: response.data.hint ?? {},
      });

    default: {
      const errors = objectKeysToCamelCase(error.errors);
      return send({
        type: Event.HAS_VALIDATION_ERRORS,
        errors: Object.keys(errors).reduce((errs, field) => {
          if (!(field in context.account)) {
            return {...errs, [field]: errors[field]};
          }

          return {
            ...errs,
            account: {
              // @ts-ignore - Not sure how to make TS like this
              ...errs.account,
              [field]: errors[field],
            },
          };
        }, {}),
      });
    }
  }
};

export const services: Services = {
  updateRegistration:
    (context, event): InvokeCallback =>
    // @ts-ignore
    async send => {
      const {token} = context;
      // @ts-ignore
      const {payload = {}, include = []} = event;
      const includes = ['contacts', 'practice_records', ...include];

      (
        await context.apiClient.post(
          `/global_users/registration?include=${includes}`,
          outgoingAccount({...payload, token})
        )
      )
        .mapData(async data => {
          send({
            type: Event.UPDATE_DATA,
            payload: transformRegistration(data),
          });
        })
        .mapErr(async error => {
          // A 404 from register account could indicate that the user already completed registration.
          // Tell the machine that we found such an error and allow it to handle what happens next.
          if (error.kind === ApiErrorKind.NotFoundError) {
            // If we don't actually have a token, then nothing more can be done. Rethrow the error.
            if (!token) {
              send({
                type: Event.UNHANDLED_ERROR,
                message: lang.general.noRegistrationDataError,
                error,
              });
              return;
            }

            /**
             * TODO: Enhance this response with the email/username the user already registered with.
             * @see https://practicegenius.atlassian.net/browse/PAT-9162
             */
            (
              await context.apiClient.get(
                `/global_users/registration/${context.token}`
              )
            ).mapData(data => {
              if (data.item?.registration?.completed) {
                send(Event.SKIP_TO_ALREADY_REGISTERED);
              } else {
                context.navigator.navigate(routes.LinkPractice, {
                  token: context.token,
                });
              }
            });
          } else if (error.kind === ApiErrorKind.ValidationError) {
            return handleRegistrationValidationError(
              error,
              context as Context,
              send
            );
          }

          send({
            type: Event.UNHANDLED_ERROR,
            message: IMT(lang.general.registrationError.message, {
              detailedError:
                lang.general.registrationError
                  .failedUpdatingUserRegistrationInfo,
            }),
            error,
          });
        });
    },
};
